import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api, useAuth } from "@/lib/auth";
import { avatarFor } from "@/data/demo";
import { PageHeader, Icon } from "@/components/kit";
import { toast } from "sonner";

const timeShort = (iso) => {
  try { return new Date(iso).toLocaleString("en-IN", { hour: "numeric", minute: "2-digit", day: "numeric", month: "short" }); }
  catch { return ""; }
};

export default function Messages() {
  const { user } = useAuth();
  const loc = useLocation();
  const [threads, setThreads] = useState([]);
  const [members, setMembers] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const loadThreads = useCallback(() => api.get("/messages/threads").then((r) => setThreads(r.data)).catch(() => {}), []);
  useEffect(() => { loadThreads(); api.get("/community/members").then((r) => setMembers(r.data)).catch(() => {}); }, [loadThreads]);

  const openThread = useCallback(async (otherId, otherName) => {
    setActive({ other_id: otherId, other_name: otherName });
    const { data } = await api.get(`/messages/thread/${otherId}`);
    setMsgs(data.messages);
    setActive({ other_id: otherId, other_name: data.other_name });
    loadThreads();
  }, [loadThreads]);

  useEffect(() => {
    const nm = loc.state?.startName;
    if (nm && members.length) { const m = members.find((x) => x.name === nm); if (m) openThread(m.id, m.name); }
  }, [loc.state, members, openThread]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!text.trim() || !active) return;
    const body = text; setText("");
    try { const { data } = await api.post(`/messages/thread/${active.other_id}`, { body }); setMsgs(data.messages); loadThreads(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't send"); setText(body); }
  };

  const startWith = (m) => openThread(m.id, m.name);
  const notYetMessaged = members.filter((m) => !threads.find((t) => t.other_id === m.id));

  return (
    <div className="fade-up">
      <PageHeader demo={false} title="Messages" subtitle="Keep the conversation going privately with members you follow." icon="MessageCircle" color="#3B82F6" />

      <div className="grid md:grid-cols-3 gap-5">
        {/* Threads / start new */}
        <div className="space-y-4">
          <div className="card-mfx p-4" data-testid="message-threads">
            <h3 className="font-head font-semibold text-sm mb-3 flex items-center gap-2"><Icon name="Inbox" size={16} className="text-slate-400" /> Conversations</h3>
            {threads.length === 0 && <p className="text-sm text-slate-500">No conversations yet. Message someone you follow to get started.</p>}
            <div className="space-y-1">
              {threads.map((t) => (
                <button key={t.other_id} onClick={() => openThread(t.other_id, t.other_name)} data-testid="message-thread-item"
                  className={`w-full text-left rounded-xl px-3 py-2.5 flex items-center gap-3 transition-colors ${active?.other_id === t.other_id ? "bg-amber-500/12" : "hover:bg-[#1A1F2B]"}`}>
                  <img src={avatarFor(t.other_name)} alt={t.other_name} className="w-9 h-9 rounded-full object-cover" loading="lazy" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate flex items-center gap-1">{t.other_name}</div>
                    <div className="text-[11px] text-slate-500 truncate">{t.last}</div>
                  </div>
                  {t.unread > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-black text-[10px] font-bold grid place-items-center" data-testid="thread-unread">{t.unread}</span>}
                </button>
              ))}
            </div>
          </div>

          {notYetMessaged.length > 0 && (
            <div className="card-mfx p-4" data-testid="message-start-list">
              <h3 className="font-head font-semibold text-sm mb-3 flex items-center gap-2"><Icon name="UserPlus" size={16} className="text-slate-400" /> Message someone you follow</h3>
              <div className="space-y-1">
                {notYetMessaged.map((m) => (
                  <button key={m.id} onClick={() => startWith(m)} data-testid="message-start-item"
                    className="w-full text-left rounded-xl px-3 py-2 flex items-center gap-3 hover:bg-[#1A1F2B]">
                    <img src={avatarFor(m.name)} alt={m.name} className="w-8 h-8 rounded-full object-cover" loading="lazy" />
                    <span className="text-sm font-medium flex-1 truncate">{m.name}</span>
                    <Icon name="Send" size={15} className="text-slate-500" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Conversation */}
        <div className="md:col-span-2 card-mfx flex flex-col h-[62vh]" data-testid="message-conversation">
          {!active ? (
            <div className="flex-1 grid place-items-center text-center p-8">
              <div>
                <div className="w-14 h-14 rounded-2xl bg-amber-500/12 grid place-items-center mx-auto mb-3"><Icon name="MessagesSquare" size={26} className="text-amber-400" /></div>
                <p className="text-slate-400 text-sm">Pick a conversation or start a new one with a member you follow.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 px-5 h-14 border-b border-[#222938]">
                <img src={avatarFor(active.other_name)} alt={active.other_name} className="w-9 h-9 rounded-full object-cover" />
                <div className="font-head font-semibold flex items-center gap-1">{active.other_name}<Icon name="BadgeCheck" size={15} className="text-blue-500" /></div>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="message-list">
                {msgs.map((m) => (
                  <div key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${m.mine ? "grad-amber text-black" : "bg-[#1A1F2B] text-slate-100"}`} data-testid={m.mine ? "msg-mine" : "msg-theirs"}>
                      <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                      <div className={`text-[10px] mt-1 ${m.mine ? "text-black/60" : "text-slate-500"}`}>{timeShort(m.ts)}</div>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="flex items-center gap-2 p-3 border-t border-[#222938]">
                <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="message-input"
                  placeholder="Write a message…" className="flex-1 rounded-xl bg-[#141822] border border-[#2E384D] px-3 py-2.5 text-sm outline-none focus:border-amber-500/40 text-slate-100" />
                <button onClick={send} data-testid="message-send-btn" className="grad-amber text-black rounded-xl px-4 py-2.5 font-semibold amber-glow"><Icon name="Send" size={16} /></button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
